import { map } from "lodash";
import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Box,
  Chip,
  Hidden,
} from "@mui/material";
import { format } from "date-fns";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { useAuth } from "../../hooks/useAuth";

import {
  fetchMaintenanceLogs,
  cancelFetchMaintenaceLogs,
} from "../../redux/slices/logs";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const equipment = [
  {
    type: "Diesel Powered (Non-pump)",
    forms: [
      {
        title: "Daily Forklift Inspection",
        path: "/forms/dailyForklift",
      },
      {
        title: "Air Compressor Checklist",
        path: "/forms/airCompressor",
      },
      {
        title: "Light Tower Checklist",
        path: "/forms/lightTower",
      },
    ],
  },
  {
    type: "Pumps",
    forms: [
      {
        title: "TEE Extreme Triplex Pump",
        path: "/forms/teeXtreme",
      },
      {
        title: "6x5 Centrifugal Pump Checklist",
        path: "/forms/6x5centrifugal",
      },
      {
        title: "Hydroplex 550 Test Pump",
        path: "/forms/550testPump",
      },
      {
        title: "Diesel Trash Pump Checklist",
        path: "/forms/dieselTrashPump",
      },
      {
        title: "Air Diaphragm Transfer Pump",
        path: "/forms/aodPump",
      },
      {
        title: "Non Metallic Air Diaphragm Transfer Pump Checklist",
        path: "/forms/aodPumpNonmetal",
      },
    ],
  },
  {
    type: "Tanks and Busters",
    forms: [
      {
        title: "Tanks Checklist",
        path: "/forms/tanksChecklist",
      },
      {
        title: "Gas Buster Checklist",
        path: "/forms/gasBuster",
      },
    ],
  },
  {
    type: "Cranes",
    forms: [
      {
        title: "Daily Crane Inspection",
        path: "/forms/dailyCrane",
      },
    ],
  },
];

function ControlledAccordion() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Card mb={6}>
      <CardContent>
        <Typography variant="h6">Maintenance Forms</Typography>
        <Typography
          variant="caption"
          sx={{ color: (t) => t.palette.text.secondary }}
        >
          Click on an equipment type to select necessary forms
        </Typography>
        <div>
          {equipment.map((equipmentObject, equipmentIndex) => {
            const { type, forms } = equipmentObject;
            return (
              <Accordion
                expanded={expanded === type}
                onChange={handleChange(type)}
                key={type}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{type}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {forms.map((form) => (
                      <ListItem
                        button
                        onClick={() => navigate(form.path)}
                        key={JSON.stringify(form)}
                      >
                        <ListItemText primary={form.title} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>{" "}
      </CardContent>
    </Card>
  );
}

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function BasicTabs() {
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={value} onChange={handleChange}>
          <Tab label="Log" {...a11yProps(0)} />
          <Tab label="Forms" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <DataGridTable />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <ControlledAccordion />
      </CustomTabPanel>
    </Box>
  );
}

function DataGridTable() {
  const navigate = useNavigate();
  const records = useSelector((s) => s.logs?.maintenance);
  const displayData = map(records, (r, rId) => {
    const {
      firstName,
      lastName,
      date,
      serial,
      issue,
      certified,
      oem,
      type,
      location,
      formType,
    } = r;
    return {
      date: format(new Date(date), "MM/dd/yy"),
      asset: serial,
      description: `${oem} ${type} Forkilift`,
      by: `${firstName} ${lastName}`,
      type: formType,
      status:
        !issue && certified ? (
          <Chip label="Completed" color="success" size="small" />
        ) : (
          <Chip label="Issue" color="warning" size="small" />
        ),
      location,
      id: rId,
    };
  });
  return (
    <Card>
      <CardContent>
        <Grid
          container
          spacing={1}
          sx={{
            mb: 2,
            paddingBottom: 2,
            borderBottom: 1,
            borderColor: (t) => t.palette.divider,
          }}
        >
          <Grid item xs={2} sm={1}>
            Date
          </Grid>
          <Grid item xs={2} sm={1}>
            Asset
          </Grid>

          <Grid item xs={4}>
            Description
          </Grid>
          <Hidden only="xs">
            <Grid item xs={2}>
              Type
            </Grid>
          </Hidden>
          <Grid item xs={2}>
            By
          </Grid>
          <Grid item xs={1}>
            Status
          </Grid>
        </Grid>
        {map(displayData, (data) => {
          const { id, date, asset, description, type, by, status } = data;
          const go = () => navigate(`/forms/${type}/${id}`);
          return (
            <Grid
              container
              key={id}
              spacing={1}
              mb={1}
              alignItems="center"
              onClick={go}
            >
              <Grid item xs={2} sm={1}>
                {date}
              </Grid>
              <Grid item xs={2} sm={1} sx={{ textAlign: "center" }}>
                {asset}
              </Grid>

              <Grid item xs={4}>
                {description}
              </Grid>
              <Hidden only="xs">
                <Grid item xs={2}>
                  {type}
                </Grid>
              </Hidden>
              <Grid item xs={2}>
                {by}
              </Grid>
              <Grid item xs={1}>
                {status}
              </Grid>
            </Grid>
          );
        })}
      </CardContent>
    </Card>
  );
}

function Maintenance() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchMaintenanceLogs());
    return () => dispatch(cancelFetchMaintenaceLogs());
  }, []);
  return (
    <React.Fragment>
      <Helmet title="Maintenance" />
      <Typography variant="h3" gutterBottom display="inline">
        Maintenance
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Dashboard
        </Link>
        <Link component={NavLink} to="/">
          Pages
        </Link>
        <Typography>Maintenance</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <BasicTabs />
        </Grid>
        <Grid item xs={12}></Grid>
      </Grid>
    </React.Fragment>
  );
}

export default Maintenance;
